import TextInput from '../../components/ui/TextInput'
import HTMLReactParser from 'html-react-parser'
import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams, useOutletContext } from 'react-router-dom'
import { authService } from '../../services/auth.service'
import { SplitScreenContext } from '../../layouts/SplitScreenLayout'
import { ErrorCode } from '@ivy-monorepo/shared'

interface OutletContext {
  setTitle: (title: string) => void
  setMessage: (message: string) => void
}

const UpdatePasswordPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [password2, setPassword2] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [isSuccessful, setIsSuccessful] = useState(false)
  const { setTitle, setMessage } = useOutletContext<OutletContext>()

  const token = searchParams.get('token')
  const email = searchParams.get('email')

  useEffect(() => {
    if (!token || !email) {
      setError(
        `The link does not contain the required information. Please try copying and pasting the link directly from the email you received.`
      )
    }
  }, [token, email])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    if (password !== password2) {
      setError('The passwords do not match.')
      setLoading(false)
      return
    }

    try {
      await authService.updatePassword(token!, email!, password)
      setIsSuccessful(true)
      setTitle('Password Updated!')
      setMessage(
        'Your password has been successfully updated. You can now log in with your new password.'
      )
      setTimeout(() => {
        navigate(`/login?email=${encodeURIComponent(email!)}`)
      }, 4000)
    } catch (err: any) {
      if (err.code === ErrorCode.AccountPasswordUsedPreviously) {
        setError('This password has been used before. Please choose a different password.')
      } else {
        setError(err.message || 'Failed to update password')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="error-message">{HTMLReactParser(error)}</div>
      {!isSuccessful && (
        <>
          <form onSubmit={handleSubmit}>
            <TextInput
              required={true}
              type="password"
              label="New Password"
              name="password"
              value={password}
              onChange={(value: string) => setPassword(value)}
            />
            <TextInput
              required={true}
              type="password"
              label="Confirm New Password"
              name="password2"
              value={password2}
              onChange={(value: string) => setPassword2(value)}
            />
            <button type="submit" className="styled-button" disabled={loading}>
              {loading ? 'Updating...' : 'Submit'}
            </button>
          </form>
          <div className="muted mt-4">
            Remember your password?{' '}
            <Link className="simple-link" to="/login">
              Return to login
            </Link>
          </div>
        </>
      )}
    </>
  )
}

export const updatePasswordContext: SplitScreenContext = {
  id: 'update-password',
  title: 'Update Your Password',
  message: 'Enter your new password below.',
  contentSide: 'left',
}

export default UpdatePasswordPage
